import { KeyboardEvent, useRef, useState } from 'react';
import {
  AlertTriangle,
  LoaderCircle,
  MinusCircleIcon,
  PlusCircleIcon,
  Keyboard,
  Layers,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import {
  addCaseToPallet,
  getPalletCases,
  getProductsByCaseId,
  removeCaseFromPallet,
} from 'services/api/palletBuild';
import { PendingCarton, RTVWMSCarton } from 'types';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';

export const AssignCasesPage = () => {
  const [scanValue, setScanValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [caseNotFound, setCaseNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const {
    pallet_id,
    pallet,
    caseId,
    caseProducts,
    isCasePending,
    assignedCases,
    isPalletPending,
    pendingCartons,
    buildPendingPallet,
  } = usePalletBuildStore();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);

  const isRemoveMode = mode === 'remove';
  const isPalletClosed = pallet?.status === 'closed';
  const displayCases = [
    ...assignedCases.map((c) => ({ ...c, pending: false })),
    ...pendingCartons.map((c) => ({
      carton_id: c.carton_id,
      product_cnt: c.products.length,
      pending: true,
    })),
  ];

  const setAssignedCases = (cases: RTVWMSCarton[], newCartonCnt: number) =>
    usePalletBuildStore.setState({ assignedCases: cases });

  const addPendingCarton = (carton: PendingCarton) =>
    usePalletBuildStore.setState((state) => ({
      pendingCartons: [
        ...state.pendingCartons.filter((c) => c.carton_id !== carton.carton_id),
        carton,
      ],
    }));

  const removePendingCarton = (cartonId: string) =>
    usePalletBuildStore.setState((state) => ({
      pendingCartons: state.pendingCartons.filter((c) => c.carton_id !== cartonId),
    }));

  const handleSubmit = async () => {
    if (isPalletClosed) return;
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setScanValue('');
    setHasError(false);
    setCaseNotFound(false);
    const failureMessage = `Failed to ${isRemoveMode ? 'remove' : 'add'} case. Please try again.`;
    const isPendingCarton = pendingCartons.some((c) => c.carton_id === trimmed);

    if (isRemoveMode) {
      if (isPendingCarton) {
        removePendingCarton(trimmed);
        playCallbackSound();
        toast.success('Case removed');
        return;
      }

      setLoading(true);
      try {
        const result = await removeCaseFromPallet(pallet_id!, trimmed);
        if (!result.success) {
          setErrorMessage(result?.message || failureMessage);
          setHasError(true);
          playErrorSound();
          return;
        }
        const casesResult = await getPalletCases(pallet_id!);
        if (!casesResult.success) {
          setErrorMessage(casesResult?.message || 'Failed to refresh cases. Please try again.');
          setHasError(true);
          playErrorSound();
          return;
        }
        setAssignedCases(casesResult.data.result, casesResult.data.cartonCnt);
        playCallbackSound();
        toast.success('Case removed from pallet');
      } catch {
        setErrorMessage(failureMessage);
        setHasError(true);
        playErrorSound();
      } finally {
        setLoading(false);
      }
      return;
    }

    if (trimmed === caseId && isCasePending) {
      addPendingCarton({ carton_id: trimmed, products: caseProducts });
      playGoodSound();
      toast.success('Case added — will be saved with the pallet');
      return;
    }

    setLoading(true);
    try {
      const caseCheck = await getProductsByCaseId(trimmed);
      if (!caseCheck.data?.cartonExist) {
        setHasError(true);
        setCaseNotFound(true);
        playErrorSound();
        return;
      }
      if (isPalletPending) {
        addPendingCarton({ carton_id: trimmed, products: caseCheck.data.result });
        playGoodSound();
        toast.success('Case added — will be built with the pallet');
        return;
      }
      const result = await addCaseToPallet(pallet_id!, trimmed);
      if (!result.success) {
        setErrorMessage(failureMessage);
        setHasError(true);
        playErrorSound();
        return;
      }
      const casesResult = await getPalletCases(pallet_id!);
      if (!casesResult.success) {
        setErrorMessage('Failed to refresh cases. Please try again.');
        setHasError(true);
        playErrorSound();
        return;
      }
      setAssignedCases(casesResult.data.result, casesResult.data.cartonCnt);
      playGoodSound();
      toast.success('Case added to pallet');
    } catch {
      setErrorMessage(failureMessage);
      setHasError(true);
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleDone = async () => {
    if (pendingCartons.length === 0) {
      if (isPalletPending) {
        setErrorMessage('Add at least one case before building the pallet.');
        setHasError(true);
        return;
      }
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
      return;
    }
    setBuilding(true);
    try {
      await buildPendingPallet();
      toast.success(isPalletPending ? 'Pallet built successfully' : 'Cases saved to pallet');
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to build pallet. Please try again.'
      );
      setHasError(true);
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col gap-4 px-6 pb-6 pt-4',
        isRemoveMode || hasError
          ? DISPOSITION_COLORS.PROBLEM.gradient
          : DISPOSITION_COLORS.CONFIRMING.gradient
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
          Pallet Build · Step 2
        </span>
        <h2 className="text-2xl font-bold text-white">Assign Cases</h2>
        {isPalletClosed ? (
          <p className="text-3xl font-bold text-white text-center">
            Pallet is closed — cases cannot be added or removed
          </p>
        ) : (
          hasError &&
          !caseNotFound && (
            <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>
          )
        )}
      </div>

      {caseNotFound && (
        <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-white" />
          <div className="flex flex-1 flex-col min-w-0">
            <span className="text-sm font-bold text-white">Case not found</span>
            <span className="text-xs text-white/80">Create the case first, then come back.</span>
          </div>
          <Button
            type="button"
            onClick={() => setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_CASE_SCAN)}
            className="h-9 shrink-0 rounded-lg bg-white px-3 text-xs font-bold text-red-700 hover:bg-white/90"
          >
            Go to Manage Case
          </Button>
        </div>
      )}

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
          <Layers className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            Pallet
          </span>
          <span className="font-mono text-sm font-bold text-slate-800 truncate">
            {pallet_id ?? '—'}
          </span>
        </div>
        {isPalletPending && (
          <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
            New
          </span>
        )}
      </div>

      {isPalletPending && (
        <div className="rounded-xl border border-emerald-100 bg-white px-4 py-2.5">
          <p className="text-xs text-emerald-600">
            This pallet doesn&apos;t exist yet. Cases you add are kept here and the pallet is built
            once you press <span className="font-bold">Done</span>.
          </p>
        </div>
      )}

      {!isPalletPending && pendingCartons.length > 0 && (
        <div className="rounded-xl border border-amber-100 bg-white px-4 py-2.5">
          <p className="text-xs text-amber-700">
            {pendingCartons.length} case{pendingCartons.length !== 1 ? 's' : ''} pending — not yet
            saved to this pallet. Press <span className="font-bold">Save Cases</span> to persist
            them.
          </p>
        </div>
      )}

      <div
        className={cn(
          'flex items-center gap-3 rounded-xl px-4 py-3',
          isRemoveMode ? 'bg-rose-50' : 'bg-emerald-50'
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            isRemoveMode ? 'bg-rose-100' : 'bg-emerald-100'
          )}
        >
          {isRemoveMode ? (
            <MinusCircleIcon className="h-4 w-4 text-rose-600" />
          ) : (
            <PlusCircleIcon className="h-4 w-4 text-emerald-600" />
          )}
        </div>
        <span
          className={cn(
            'text-xl font-bold uppercase tracking-widest',
            isRemoveMode ? 'text-rose-600' : 'text-emerald-600'
          )}
        >
          {isRemoveMode ? 'Remove Case' : 'Add Case'}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Assigned Cases
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">
            {displayCases?.length || 0}
          </span>
        </div>

        {displayCases.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-4 py-8">
            <Package className="h-8 w-8 text-slate-200" />
            <p className="text-sm text-slate-400">No cases assigned yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {displayCases.map((carton) => (
              <div key={carton.carton_id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm font-medium text-slate-700 truncate">
                    {carton.carton_id}
                  </span>
                  {carton.pending && (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                      Pending
                    </span>
                  )}
                </div>
                <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                  {carton.product_cnt}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(loading || building) && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 py-3">
          <LoaderCircle className="h-5 w-5 animate-spin text-emerald-600" />
          <span className="text-sm font-medium text-slate-500">
            {building ? 'Building pallet...' : isRemoveMode ? 'Removing case...' : 'Adding case...'}
          </span>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Case Barcode</Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Scan case..."
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            disabled={isPalletClosed}
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            disabled={isPalletClosed}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        <Button
          variant="outline"
          onClick={() => setMode(isRemoveMode ? 'add' : 'remove')}
          className={cn('flex-1 h-12', {
            'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700': isRemoveMode,
            'text-rose-600 hover:bg-rose-50 hover:text-rose-700': !isRemoveMode,
          })}
        >
          Switch to {isRemoveMode ? 'Add' : 'Remove'}
        </Button>
        <Button
          onClick={handleDone}
          disabled={loading || building}
          className="bg-emerald-600 text-white hover:bg-emerald-700 flex-1 h-12"
        >
          {building
            ? 'Building...'
            : pendingCartons.length > 0
              ? isPalletPending
                ? 'Build Pallet'
                : 'Save Cases'
              : 'Done'}
        </Button>
      </div>
    </div>
  );
};
